// RF devices — the sensors that have no unique number.
//
// One panel, both shells and both entry points: the RF receiver's own screen
// (`receiverUno` set — only what that receiver hears) and the admin page
// (`receiverUno` empty — every RF sensor on the estate).
//
// Three things happen here:
//   LEARN     press the sensor, the code appears (core/hooks/useRfLearn)
//   NAME      a code gets a meaning ("Open" / "Close" / "Alarm") and lands on a
//             sensor — automatically, because the top 24 bits of the code ARE
//             the sensor (core/devices/rfCodes)
//   WATCH     what each sensor last said, and the full event log
//
// ⚠️ THE SERVER RUNS THE ACTIONS AND SENDS THE NOTIFICATIONS. This screen only
// names things. It must never publish on hearing a code — `AddRFREMOTE` already
// publishes every mapped action, and a second publish from here would fire every
// RF-driven switch twice.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Tooltip, LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SensorsIcon from '@mui/icons-material/Sensors';
import EditIcon from '@mui/icons-material/Edit';
import { useApp } from '../../core/state/AppContext';
import { getRfDevices, getRfEventLog, getRooms } from '../../core/api/api';
import useRfLearn from '../../core/hooks/useRfLearn';
import RfSensorDialog, { nameOf } from './RfSensorDialog';
import ReminderSnoozeBar from './ReminderSnoozeBar';
import RfScanDialog from './RfScanDialog';
import { rfNumber } from '../../core/devices/rfIdentify';
import { invalidateRfDirectory } from '../../core/hooks/useRfDirectory';
import { rfType, isCloseEvent } from '../../core/devices/rfTypes';
import { binaryOf, rfStateIsStale } from '../../core/devices/rfCodes';
import { parseServerTime } from '../../core/devices/firmwareInfo';

const when = (raw) => {
  const t = parseServerTime(raw);
  return t == null ? '—' : new Date(t).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
};
const ago = (raw) => {
  const t = parseServerTime(raw);
  if (t == null) return '';
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.floor(h / 24)} d ago`;
};


// One sensor's current state. ⚠️ ALWAYS WITH ITS TIME: a transmitter is not
// acknowledged, so a lost Close frame leaves "Open" standing. The app may say
// what it last HEARD, never what the door is doing.
function StateChip({ device }) {
  const t = parseServerTime(device.LastAt);
  const stale = rfStateIsStale(t);
  const event = String(device.LastEvent || '').trim();
  if (!event && !t) return <Chip size="small" variant="outlined" label="never heard" />;
  return (
    <Tooltip title={t ? `Last heard ${when(device.LastAt)}` : 'Never heard'}>
      <Chip
        size="small"
        color={stale ? 'default' : (isCloseEvent(event) ? 'success' : 'warning')}
        variant={stale ? 'outlined' : 'filled'}
        label={`${event || 'heard'} · ${ago(device.LastAt) || '—'}`}
      />
    </Tooltip>
  );
}


export default function RfDevicesPanel({ receiverUno = '', locationId = 0, compact = false }) {
  const { userId } = useApp();
  const [tab, setTab] = useState('sensors');
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { heard, clear } = useRfLearn(receiverUno);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const body = {};
      if (receiverUno) body.ReceiverUniqueNumber = receiverUno;
      else if (locationId) body.LocationID = locationId;
      const res = await getRfDevices(body);
      setDevices(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError('Could not read the RF devices. Is the backend deployed?');
    } finally {
      setLoading(false);
    }
  }, [receiverUno, locationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'log') return;
    const body = { Days: 7, Limit: 500 };
    if (receiverUno) body.ReceiverUniqueNumber = receiverUno;
    else if (locationId) body.LocationID = locationId;
    getRfEventLog(body).then((r) => setEvents(Array.isArray(r.data) ? r.data : [])).catch(() => setEvents([]));
  }, [tab, receiverUno, locationId]);

  useEffect(() => {
    if (!locationId || !userId) return;
    getRooms(userId, locationId)
      .then((r) => setRooms(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRooms([]));
  }, [userId, locationId]);

  // A code just heard that no sensor owns yet — the whole point of Learn.
  const known = useMemo(() => {
    const s = new Set();
    devices.forEach((d) => (d.Codes || []).forEach((c) => s.add(String(c.REMOTEVALUE))));
    return s;
  }, [devices]);

  const openFor = async (row) => {
    // The sensor for a freshly heard code — the server creates it on first
    // sight, so a reload is enough to find it by transmitter.
    const res = await getRfDevices(receiverUno ? { ReceiverUniqueNumber: receiverUno } : {}).catch(() => null);
    const list = Array.isArray(res?.data) ? res.data : [];
    const found = list.find((d) => Number(d.TransmitterId) === row.transmitterId);
    setDevices(list);
    if (found) {
      const codes = found.Codes && found.Codes.length ? found.Codes : [];
      const hasIt = codes.some((c) => String(c.REMOTEVALUE) === String(row.code));
      setEditing({
        ...found,
        Codes: hasIt ? codes : [...codes, {
          REMOTEVALUE: String(row.code), RemoteBinaryValue: row.binary, DeviceAction: '',
          UniqueNumber: row.receiverUno || receiverUno,
        }],
      });
    }
  };

  // A scanned sensor is saved; open its full settings so the installer picks
  // who is told and sends a test before leaving — the scan saved only the
  // essentials.
  const afterScan = async ({ RfDeviceId, transmitterId }) => {
    invalidateRfDirectory();
    // Same scope as load(), so a customer is never handed the whole estate.
    const body = receiverUno ? { ReceiverUniqueNumber: receiverUno } : (locationId ? { LocationID: locationId } : {});
    const res = await getRfDevices(body).catch(() => null);
    const list = Array.isArray(res?.data) ? res.data : [];
    load();
    const found = list.find((d) => (RfDeviceId && Number(d.RfDeviceId) === RfDeviceId)
      || Number(d.TransmitterId) === transmitterId);
    setTab('sensors');
    if (found) setEditing(found);
  };

  // A code nobody has named yet. This is the whole discovery flow on a phone:
  // the installer triggers the sensor, the banner appears wherever they are in
  // the panel, and Add names it. No admin rights — anyone who can open the
  // receiver can name what it hears.
  const unknown = heard.filter((r) => !known.has(String(r.code)));

  return (
    <Box>
      {/* What is reminding ME right now, with its snooze — this screen is where
          a tapped reminder notification lands (api/new/reminders.js `route`). */}
      <ReminderSnoozeBar />
      {unknown.length > 0 && (
        <Alert
          severity="success"
          sx={{ mb: 1 }}
          action={(
            <Button
              size="small" variant="contained" startIcon={<AddIcon />}
              onClick={() => openFor(unknown[0])}
            >
              Add
            </Button>
          )}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {unknown.length === 1 ? 'New RF device found' : `${unknown.length} new RF devices found`}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {`code ${unknown[0].code} · sensor ${unknown[0].transmitterId}`}
          </Typography>
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 13 } }}>
          <Tab value="sensors" label={`Sensors (${devices.length})`} />
          <Tab value="learn" label={`Learn${heard.length ? ` (${heard.length})` : ''}`} />
          <Tab value="log" label="Event log" />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        {/* The guided way in: say what it is, do what the app asks, name it. */}
        <Button size="small" variant="contained" startIcon={<SensorsIcon />} onClick={() => setScanning(true)}>
          Scan
        </Button>
        <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}

      {tab === 'sensors' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sensor</TableCell>
                <TableCell>Type</TableCell>
                {!compact && <TableCell>Room</TableCell>}
                <TableCell>Codes</TableCell>
                <TableCell>Last</TableCell>
                <TableCell align="center">Notify</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((d) => (
                <TableRow key={d.RfDeviceId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {`${rfType(d.RfType).icon} ${nameOf(d)}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {`${rfNumber(d.RfDeviceId)} · hw ${d.TransmitterId}`}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{rfType(d.RfType).label}</Typography></TableCell>
                  {!compact && <TableCell><Typography variant="body2">{d.RoomName || '—'}</Typography></TableCell>}
                  <TableCell>
                    {(d.Codes || []).map((c) => (
                      <Chip
                        key={c.REMOTEVALUE} size="small" sx={{ mr: 0.5, mb: 0.25 }}
                        label={c.DeviceAction ? `${c.DeviceAction}` : `${c.REMOTEVALUE}`}
                        variant={c.DeviceAction ? 'filled' : 'outlined'}
                      />
                    ))}
                    {!(d.Codes || []).length && <Typography variant="caption" color="text.secondary">none</Typography>}
                  </TableCell>
                  <TableCell><StateChip device={d} /></TableCell>
                  <TableCell align="center">
                    {Number(d.Notify) ? <Chip size="small" color="success" label="on" /> : <Chip size="small" label="off" variant="outlined" />}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setEditing(d)}><EditIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!devices.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No RF sensors yet. Open <b>Learn</b> and press one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 'learn' && (
        <Paper variant="outlined" sx={{ p: 1 }}>
          <Alert severity="info" sx={{ mb: 1 }}>
            {receiverUno
              ? `Listening to ${receiverUno}. Trigger the sensor — open the door, wave at the motion sensor — and its code appears here.`
              : 'Listening to every receiver. Trigger a sensor and its code appears here.'}
            {' '}A sensor repeats each press several times; that is counted, not listed again.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" onClick={clear}>Clear</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Sensor (hw, top 24 bits)</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Heard</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {heard.map((r) => (
                  <TableRow key={r.code} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {r.code}
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {r.binary || binaryOf(r.code)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.transmitterId}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{`#${r.eventByte}`}</TableCell>
                    <TableCell>
                      {`${new Date(r.at).toLocaleTimeString()}${r.repeats > 1 ? ` ×${r.repeats}` : ''}`}
                    </TableCell>
                    <TableCell align="right">
                      {known.has(String(r.code))
                        ? <Chip size="small" label="known" variant="outlined" />
                        : (
                          <Button size="small" startIcon={<AddIcon />} onClick={() => openFor(r)}>
                            Name it
                          </Button>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
                {!heard.length && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Waiting… press the sensor now.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tab === 'log' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Sensor</TableCell>
                <TableCell>Event</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>Code</TableCell>
                <TableCell>Receiver</TableCell>
                <TableCell align="right">Actions</TableCell>
                <TableCell align="right">Told</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.RfEventId} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(e.At)}</TableCell>
                  <TableCell>
                    {`${rfType(e.RfType).icon} ${String(e.RfDeviceName || '').trim() || `Unassigned · ${rfNumber(e.RfDeviceId) || `hw ${e.TransmitterId}`}`}`}
                    {e.RoomName && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{e.RoomName}</Typography>}
                  </TableCell>
                  <TableCell>{e.Meaning || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{e.RemoteValue}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{e.ReceiverUniqueNumber || '—'}</TableCell>
                  <TableCell align="right">{e.ActionsFired}</TableCell>
                  <TableCell align="right">{e.Notified}</TableCell>
                </TableRow>
              ))}
              {!events.length && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Nothing in the last 7 days.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <RfScanDialog
        open={scanning}
        receiverUno={receiverUno}
        locationId={locationId}
        rooms={rooms}
        devices={devices}
        onClose={() => setScanning(false)}
        onSaved={afterScan}
      />

      <RfSensorDialog
        device={editing}
        rooms={rooms}
        locationId={locationId}
        onClose={() => setEditing(null)}
        onSaved={() => { invalidateRfDirectory(); load(); }}
      />
    </Box>
  );
}
